import * as _ from 'underscore';

import S from 's-js';
import { IAjax } from 'lib/Ajax';
import TZDate from 'lib/TZDate';
import { TZUtils } from 'lib/TZUtils';
import { CachedModelLoader } from 'lib/CachedModelLoader';
import { CaseInsensitiveContains } from 'lib/diacriticals';
import { debounceSignal } from 'lib/debounceSignal';
import { tzmoment, tzmomentMod } from 'lib/tzmoment';

import { Entrypoints } from 'core/app/entrypoints';
import { ServicesDashboardResponse, ServicesDashboardResponseOptions } from 'data/models/ServicesDashboardResponse';
import { TopicCard } from 'data/models/TopicCard';
import { TopicCardStatus } from 'server/enums/TopicCardStatus';

export type ServicesDashboardLoader = CachedModelLoader<ServicesDashboardResponse, ServicesDashboardResponseOptions>;

export enum Column {
    Status,
    Time,
    Label,
    Topic,
    Vessel,
    Berth
}

export const Columns = [
    Column.Status,
    Column.Time,
    Column.Label,
    Column.Topic,
    Column.Vessel,
    Column.Berth
];

const SortFunctions : { [ key : number] : (a : TopicCard, b : TopicCard) => number } = {
    [Column.Status]: (a, b) => 
        TopicCardStatus[a.Status()] < TopicCardStatus[b.Status()] ? -1 : 1,
    [Column.Time]: (a, b) => 
        a.TimeEntry.Response() == null ? 1 :
        b.TimeEntry.Response() == null ? -1 :
        +a.TimeEntry.Response()! - +b.TimeEntry.Response()!,
    [Column.Label]: (a, b) =>
        !a.AmenityType() ? 1 :
        !b.AmenityType() ? -1 :
        a.AmenityType()!.Name() < b.AmenityType()!.Name() ? -1 :
        1,
    [Column.Topic]: (a, b) =>
        a.Title() < b.Title() ? -1 : 1,
    [Column.Vessel]: (a, b) =>
        !a.PortCall() || !a.PortCall()!.Vessel() ? 1 :
        !b.PortCall() || !b.PortCall()!.Vessel() ? -1 :
        (a.PortCall()!.DisplayVesselIdentifier() || '') < (b.PortCall()!.DisplayVesselIdentifier() || '') ? -1 :
        1,
    [Column.Berth]: (a, b) =>
        !a.Berth() || !a.Berth()!.Location() ? 1 :
        !b.Berth() || !b.Berth()!.Location() ? -1 :
        a.Berth()!.Location()!.Name() < b.Berth()!.Location()!.Name() ? -1 :
        1
}

export interface ServicesDashboardParams {
    id : number,
    date : TZDate,
    filter : string,
    sort : Column,
    reversed : boolean
}

export const ServicesDashboard = (params : ServicesDashboardParams, loader : ServicesDashboardLoader, tzutils : TZUtils) => {
    const Date = S.value(params.date),
        Today = tzutils.today(),
        dateData = S(() => loader({ OrganizationId: params.id, Date: Date().toJSON() })),
        Loading = () => dateData().loading(),
        SortReversed = S.value(params.reversed),
        Sort = S.value(params.sort),
        sortedServices = () => {
            let services = Array.from(dateData().model.Services())
            
            services.sort(SortFunctions[Sort()]);

            if (SortReversed()) services.reverse();

            return services;
        },
        Filter = S.value(params.filter),
        debouncedFilter = debounceSignal(Filter),
        DisplayedServices = S(() => !debouncedFilter() ? sortedServices() : 
            sortedServices().filter(s => 
                CaseInsensitiveContains(TopicCardStatus[s.Status()], debouncedFilter())
                || (s.TimeEntry.Response() && CaseInsensitiveContains(tzmoment(s.TimeEntry.Response()!).format('HH:mm'), debouncedFilter()))
                || (s.AmenityType() && CaseInsensitiveContains(s.AmenityType()!.Name(), debouncedFilter()))
                || CaseInsensitiveContains(s.Title(), debouncedFilter())
                || (s.PortCall() && CaseInsensitiveContains(s.PortCall()!.DisplayVesselIdentifier() || '', debouncedFilter()))
                || (s.Berth() && s.Berth()!.Location() && CaseInsensitiveContains(s.Berth()!.Location()!.Name(), debouncedFilter()))
        ));

    return { Loading, DisplayedServices, Filter, Sort, SortReversed, Date, AddDays, SortToggle, OnClickService, Today };

    function AddDays(n : number) {
        Date(tzmomentMod(tzutils, Date(), m => m.add(n, 'd')));
    }

    function SortToggle(col : Column) {
        if (col === Sort()) SortReversed(!SortReversed());
        else S.freeze(() => {
            Sort(col);
            SortReversed(false);
        });
    }

    function OnClickService(s : TopicCard) {
        window.location.hash = "#!topiccards?id=" + s.PortCallId() + '&cardId=' + s.Id();
    }
}
export type ServicesDashboard = ReturnType<typeof ServicesDashboard>;


// WEBPACK FOOTER //
// ./apps/portcall/berthmgrdashboard/servicesdashboard.ts